import { useEffect, useState } from 'react'

/** Rows of item cards revealed per scroll batch (× grid columns: 15 on phones, 20 from `xl`). */
export const INFINITE_BATCH_ROWS = 5

/**
 * Infinite scroll over an in-memory list: shows `batchSize` items, then another batch each time
 * the sentinel nears the viewport. Starts over when `resetKey` changes (new filter / sort).
 */
export function useInfiniteBatch(total: number, batchSize: number, resetKey: string) {
  const [count, setCount] = useState(batchSize)
  // Callback ref (state) so the observer attaches whenever the sentinel mounts, even mid-batch.
  const [sentinel, sentinelRef] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setCount(batchSize)
  }, [resetKey, batchSize])

  const visible = Math.min(count, total)
  const hasMore = visible < total

  // Re-observe after every batch so a sentinel that is still on screen (short content) keeps loading.
  useEffect(() => {
    if (!sentinel || !hasMore) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setCount((c) => c + batchSize)
      },
      { rootMargin: '600px 0px' }
    )
    io.observe(sentinel)
    return () => io.disconnect()
  }, [sentinel, hasMore, batchSize, visible])

  return { visible, hasMore, sentinelRef }
}
